import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { MediaTitle } from '@/types';

interface HeroCarouselProps {
  items: MediaTitle[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);

  const goTo = useCallback(
    (next: number) => {
      if (items.length === 0) return;
      setIndex(((next % items.length) + items.length) % items.length);
    },
    [items.length],
  );

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(() => setIndex((i) => (i + 1) % items.length), 6000);
    return () => clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) {
    return (
      <div className="flex h-[60vh] min-h-[420px] items-center justify-center bg-primary-950 text-background-300">
        暂无相关影视资源，敬请期待
      </div>
    );
  }

  const active = items[index];

  return (
    <section className="relative h-[560px] w-full overflow-hidden bg-primary-950 md:h-[74vh] md:min-h-[560px]">
      {items.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
        >
          <img
            src={item.backdrop}
            alt={`${item.title} 剧照`}
            title={`${item.title} - 星辰影院`}
            className="h-full w-full scale-105 object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/65 to-primary-950/25" />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-transparent to-primary-950/40" />
        </div>
      ))}

      <div className="relative z-10 mx-auto flex h-full w-full max-w-[1280px] flex-col justify-center px-4 pb-16 md:px-8">
        <div key={active.id} className="max-w-xl animate-fade-up">
          <div className="flex flex-wrap items-center gap-2.5">
            <span className="rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-primary-950">
              {active.type === 'tv' ? '热播剧集' : '焦点大片'}
            </span>
            <span className="flex items-center gap-1 rounded-full bg-black/40 px-3 py-1 text-xs font-medium text-background-100 backdrop-blur-sm">
              <i className="ri-star-fill text-accent-400" />
              {active.rating.toFixed(1)} 分
            </span>
          </div>

          <h1 className="mt-4 font-heading text-3xl font-extrabold leading-tight text-background-50 text-glow md:text-5xl">
            {active.title}
          </h1>

          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm text-background-200">
            <span>{active.year}</span>
            <span className="text-background-500">·</span>
            <span>{active.region}</span>
            <span className="text-background-500">·</span>
            <span>{active.type === 'tv' ? `${active.seasons}季 ${active.episodes}集` : active.duration}</span>
            <span className="text-background-500">·</span>
            <span>{active.genres.join(' / ')}</span>
          </div>

          <p className="mt-4 line-clamp-3 max-w-lg text-sm leading-relaxed text-background-300 md:text-base">
            {active.summary}
          </p>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${active.id}`}
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
            >
              <i className="ri-play-circle-line text-lg" />
              查看详情
            </Link>
            <Link
              to={active.type === 'tv' ? '/tvshows' : '/movies'}
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 bg-black/25 px-6 py-3 text-sm font-semibold text-background-50 backdrop-blur-sm transition-colors hover:bg-black/40"
            >
              <i className="ri-compass-3-line text-lg" />
              浏览同类
            </Link>
          </div>
        </div>
      </div>

      {/* controls */}
      <button
        type="button"
        aria-label="上一张"
        onClick={() => goTo(index - 1)}
        className="absolute left-3 top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/35 text-background-50 backdrop-blur-sm transition-colors hover:bg-black/55 md:flex lg:left-6"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => goTo(index + 1)}
        className="absolute right-3 top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/35 text-background-50 backdrop-blur-sm transition-colors hover:bg-black/55 md:flex lg:right-6"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      <div className="absolute bottom-7 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2.5">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到 ${item.title}`}
            onClick={() => goTo(i)}
            className={`h-1.5 cursor-pointer rounded-full transition-all duration-300 ${
              i === index ? 'w-8 bg-accent-500' : 'w-3 bg-background-50/50 hover:bg-background-50/80'
            }`}
          />
        ))}
      </div>
    </section>
  );
}